/**
 * Centralized EC Calculation Service
 *
 * SINGLE SOURCE OF TRUTH for all electrical conductivity calculations
 * Implements EXACT Pascal HydroBuddy algorithms from hb_main.pas
 *
 * This service eliminates scattered EC calculations and ensures consistency
 * across all components (calculator, stock solutions, reports, etc.)
 */

// Configuration constants - can be overridden via environment or user settings
const EC_CONFIG = {
  IONIC_STRENGTH_COEFFICIENT: -0.7025187, // Activity correction coefficient
  CONCENTRATION_FACTOR_PPM: 1, // Factor for ppm units
  MOLAR_MASS_DIVISOR: 1000, // Convert ppm to molarity
  DEFAULT_TEMPERATURE: 25, // Celsius - for future temperature corrections
  PRECISION_DECIMAL_PLACES: 3 // EC result precision
};

export interface ElementConcentration {
  symbol: string;
  concentration: number; // ppm
}

export interface ECResult {
  predicted: number;      // mS/cm
  model: string;         // 'LMCv2' or 'Empirical'
  ionicStrength: number; // mol/L
  breakdown: Array<{
    element: string;
    contribution: number;
    percentage: number;
  }>;
}

export class ECCalculationService {
  // EXACT Pascal constants from hb_main.pas
  private static readonly PASCAL_MOLAR_MASSES = [
    14.007,  // N (NO3-) - index 0
    14.007,  // N (NH4+) - index 1  
    30.974,  // P - index 2
    39.098,  // K - index 3
    24.305,  // Mg - index 4
    40.078,  // Ca - index 5
    32.066,  // S - index 6
    55.845,  // Fe - index 7
    54.938,  // Mn - index 8
    65.409,  // Zn - index 9
    63.546,  // Cu - index 10
    10.811,  // B - index 11
    95.94,   // Mo - index 12
    28.086,  // Si - index 13
    35.453,  // Cl - index 14
    22.990   // Na - index 15
  ];

  // EXACT Pascal zi values (ionic charges)
  private static readonly PASCAL_IONIC_CHARGES = [
    1, 1, 3, 1, 2, 2, 2, 2, 2, 2, 2, 3, 2, 4, 1, 1
  ];

  // EXACT Pascal LMCv2 EC contributions (lines 1052-1056)
  private static readonly PASCAL_LMCV2_CONTRIBUTIONS = [
    71.46,  // N (NO3-)
    73.5,   // N (NH4+)
    57,     // P
    73,     // K
    106,    // Mg
    119,    // Ca
    160,    // S
    108.0,  // Fe
    0,      // Mn (not conductive at hydroponic pH)
    0,      // Zn (not conductive at hydroponic pH)
    0,      // Cu (not conductive at hydroponic pH)
    0,      // B (not conductive at hydroponic pH)
    0,      // Mo (not conductive at hydroponic pH)
    0,      // Si (does not conduct at hydroponic pH)
    76.35,  // Cl
    50.01   // Na
  ];

  // EXACT Pascal Empirical EC contributions (lines 1028-1039)
  private static readonly PASCAL_EMPIRICAL_CONTRIBUTIONS = [
    0.403791,   // N (NO3-)
    -0.308967,  // N (NH4+)
    0.142525,   // P
    -0.110791,  // K
    -0.358782,  // Mg
    -0.283549,  // Ca
    0.276118,   // S
    0, 0, 0, 0, 0, 0, 0, 0, 0  // Fe through Na (all zeros)
  ];

  // Element symbol mapping for DBF compatibility
  // N_NO3 and N_NH4 are distinct ions (different λ). Plain "N" is total N (NH4+NO3)
  // and must NOT be forced into the NO3 conductivity slot alone.
  private static readonly ELEMENT_MAPPINGS: { [key: string]: number } = {
    // Nitrate-N only → Pascal index 0
    'N_NO3': 0, 'N(NO3-)': 0, 'NNO3': 0, 'NO3': 0,
    // Ammonium-N only → Pascal index 1
    'N_NH4': 1, 'N(NH4+)': 1, 'NNH4': 1, 'NH4': 1,
    // Plain 'N' omitted on purpose — total N is not a single conductivity species
    // Major elements
    'P': 2, 'K': 3, 'Ca': 5, 'CA': 5, 'Mg': 4, 'MG': 4, 'S': 6,
    // Microelements
    'Fe': 7, 'FE': 7, 'Mn': 8, 'MN': 8, 'Zn': 9, 'ZN': 9,
    'Cu': 10, 'CU': 10, 'B': 11, 'Mo': 12, 'MO': 12,
    // Secondary elements
    'Si': 13, 'SI': 13, 'Cl': 14, 'CL': 14, 'Na': 15, 'NA': 15
  };

  /**
   * Calculate EC using Pascal HydroBuddy LMCv2 model (DEFAULT)
   * EXACT implementation from hb_main.pas lines 1041-1059
   */
  static calculateLMCv2(concentrations: ElementConcentration[]): ECResult {
    // Step 1: Calculate ionic strength (Pascal lines 1046-1047)
    let ionicStrength = 0;
    const elementCount = this.PASCAL_MOLAR_MASSES.length; // Dynamic element count
    const elementConcentrations = new Array(elementCount).fill(0);

    // Map concentrations to Pascal element indices
    concentrations.forEach(conc => {
      const elementIndex = this.getElementIndex(conc.symbol);
      if (elementIndex >= 0) {
        elementConcentrations[elementIndex] = conc.concentration;
        const molarity = conc.concentration / (EC_CONFIG.MOLAR_MASS_DIVISOR * this.PASCAL_MOLAR_MASSES[elementIndex]);
        ionicStrength += this.PASCAL_IONIC_CHARGES[elementIndex] ** 2 * molarity;
      }
    });

    // Step 2: Calculate EC with ionic strength correction (Pascal lines 1049-1057)
    let predictedEC = 0;
    const breakdown: ECResult['breakdown'] = [];

    for (let i = 0; i < elementCount; i++) {
      const concentration = elementConcentrations[i];
      if (concentration > 0 && this.PASCAL_LMCV2_CONTRIBUTIONS[i] > 0) {
        const concentrationFactor = EC_CONFIG.CONCENTRATION_FACTOR_PPM; // For ppm units (Pascal line 791)
        const molarity = concentration / (EC_CONFIG.MOLAR_MASS_DIVISOR * this.PASCAL_MOLAR_MASSES[i]);
        const activityCorrection = Math.exp(
          EC_CONFIG.IONIC_STRENGTH_COEFFICIENT * Math.sqrt(ionicStrength) * (this.PASCAL_IONIC_CHARGES[i] ** 1.5)
        );

        // EXACT Pascal formula
        const contribution = concentrationFactor * molarity * 
                           this.PASCAL_LMCV2_CONTRIBUTIONS[i] * activityCorrection;
        
        predictedEC += contribution;
        
        breakdown.push({
          element: this.getElementSymbol(i),
          contribution: contribution,
          percentage: 0 // Will be calculated after total
        });
      }
    }

    // Calculate percentages
    breakdown.forEach(item => {
      item.percentage = predictedEC > 0 ? (item.contribution / predictedEC) * 100 : 0;
    });

    return {
      predicted: Math.round(predictedEC * 1000) / 1000, // Round to 3 decimal places
      model: 'LMCv2',
      ionicStrength: Math.round(ionicStrength * 1000000) / 1000000,
      breakdown
    };
  }

  /**
   * Calculate EC using Pascal HydroBuddy Empirical model
   * EXACT implementation from hb_main.pas lines 1028-1039
   */
  static calculateEmpirical(concentrations: ElementConcentration[]): ECResult {
    let predictedEC = 0;
    const breakdown: ECResult['breakdown'] = [];

    concentrations.forEach(conc => {
      const elementIndex = this.getElementIndex(conc.symbol);
      if (elementIndex >= 0) {
        const concentrationFactor = EC_CONFIG.CONCENTRATION_FACTOR_PPM; // For ppm units
        const contribution = concentrationFactor * conc.concentration * 
                           this.PASCAL_EMPIRICAL_CONTRIBUTIONS[elementIndex];
        
        if (contribution !== 0) {
          predictedEC += contribution;
          breakdown.push({
            element: conc.symbol,
            contribution: contribution,
            percentage: 0 // Will be calculated after total
          });
        }
      }
    });

    // Add empirical constant (Pascal line 1038)
    predictedEC += 0.39661671;

    // Calculate percentages
    breakdown.forEach(item => {
      item.percentage = predictedEC > 0 ? (item.contribution / predictedEC) * 100 : 0;
    });

    return {
      predicted: Math.round(predictedEC * 1000) / 1000, // Round to 3 decimal places
      model: 'Empirical',
      ionicStrength: 0, // Not calculated in empirical model
      breakdown
    };
  }

  /**
   * Main EC calculation method - uses LMCv2 by default
   */
  static calculate(
    concentrations: ElementConcentration[], 
    model: 'LMCv2' | 'Empirical' = 'LMCv2'
  ): ECResult {
    if (model === 'Empirical') {
      return this.calculateEmpirical(concentrations);
    } else {
      return this.calculateLMCv2(concentrations);
    }
  }

  /**
   * Get Pascal element index from symbol (handles DBF variations)
   */
  private static getElementIndex(symbol: string): number {
    const normalizedSymbol = symbol.toUpperCase().replace(/[^A-Z0-9()+-]/g, '');
    return this.ELEMENT_MAPPINGS[normalizedSymbol] ?? this.ELEMENT_MAPPINGS[symbol] ?? -1;
  }

  /**
   * Get element symbol from Pascal index
   */
  private static getElementSymbol(index: number): string {
    const symbols = ['N', 'N_NH4', 'P', 'K', 'Mg', 'Ca', 'S', 'Fe', 'Mn', 'Zn', 'Cu', 'B', 'Mo', 'Si', 'Cl', 'Na'];
    return symbols[index] || 'Unknown';
  }

  /**
   * Validate EC result for realistic hydroponic ranges
   */
  static validateResult(result: ECResult): { isValid: boolean; warnings: string[] } {
    const warnings: string[] = [];
    let isValid = true;

    if (result.predicted < 0.1) {
      warnings.push('EC too low for hydroponic cultivation (< 0.1 mS/cm)');
      isValid = false;
    }

    if (result.predicted > 5.0) {
      warnings.push('EC too high for most crops (> 5.0 mS/cm)');
      isValid = false;
    }

    if (result.ionicStrength > 0.1) {
      warnings.push('Very high ionic strength may affect nutrient availability');
    }

    return { isValid, warnings };
  }
}
